import { Body, Heading } from '@assembly-js/design-system';
import React from 'react';

import { BillingIntervalToggle } from './BillingIntervalToggle';
import { StudioBillingInterval } from './studioPlanFeatures';

/**
 * The row's column rule, shared with the loading silhouettes so the layout
 * doesn't jump when the data lands.
 *
 * The 280px floor is what a card needs to hold its own CTA on one line: the
 * longest label ("Downgrade to Professional") measures 180px, over 24px of
 * button padding and 40px of card padding. Below that the label — which cannot
 * wrap — overflows the button it sits in, which is how the row read as
 * misaligned at tablet widths.
 */
export const PLAN_ROW_COLUMNS_CLASS_NAME =
  'grid gap-5 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]';

interface PlanGridSectionProps {
  title: string;
  subtitle?: string;
  interval: StudioBillingInterval;
  onChangeInterval: (interval: StudioBillingInterval) => void;
  children: React.ReactNode;
}

/**
 * The "choose your plan" section both audiences render: heading, billing
 * toggle, and the card row itself. The row's cards inherit its rows (see
 * {@link PlanCardShell}), which is what keeps their dividers and CTAs aligned —
 * so the grid definition has to live in one place or the two audiences drift
 * apart again.
 */
export const PlanGridSection: React.FC<PlanGridSectionProps> = ({
  title,
  subtitle,
  interval,
  onChangeInterval,
  children,
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-1">
        <Heading size="lg" tag="h2" className="mb-0! text-black-heading">
          {title}
        </Heading>
        {subtitle && (
          <Body className="leading-[22px] text-gray-small">{subtitle}</Body>
        )}
      </div>
      <BillingIntervalToggle
        interval={interval}
        onChangeInterval={onChangeInterval}
      />
    </div>
    {/* Tracks size off the row's own width rather than the window's: a
        resizable nav sits between the two, so a wider window can leave this row
        narrower — which is how a viewport breakpoint squeezed three cards into a
        tablet's worth of space. The floor is whatever keeps a card wide enough
        for its CTA, and auto-fit collapses the tracks it can't fill so the cards
        divide the row evenly however many there are. Rows stay implicit for each
        card to claim a pair of and subgrid onto (see PlanCardShell). */}
    <div className={PLAN_ROW_COLUMNS_CLASS_NAME}>{children}</div>
  </div>
);
